import { Field, Area, Section } from "./fields";
import { Input } from "../ui/input";

export const TASKS = [
  "Meal Preparation", "Housework", "Finances", "Medications", "Shopping", "Transportation",
  "Hygiene", "Dressing - Upper Body", "Dressing - Lower Body", "Locomotion", "Transfers",
  "Toilet Use", "Bed Mobility", "Eating", "Bladder Incontinence", "Bowel Incontinence",
  "Personal Care", "Bathing - Wash-up", "Bathing - Shower", "Lotion Application", "Laundry",
  "Reading/Writing", "Supervision - Daytime", "Supervision - Nighttime",
];

export const LOC_LABELS = ["No Help", "Reminders", "Supervision", "Standby Assist", "Hands-On"];

export const emptyFunctional = {
  assessor: "", assessment_date: "", general_notes: "",
  tasks: TASKS.map((name) => ({ name, level: 0, days_times: "", notes: "" })),
};

const LOC_COLORS = [
  "bg-[#27AE60]/15 text-[#1e8449] border-[#27AE60]/40",
  "bg-stone-100 text-stone-700 border-stone-300",
  "bg-[#D68910]/15 text-[#9a6a0b] border-[#D68910]/40",
  "bg-orange-100 text-orange-800 border-orange-300",
  "bg-[#C0392B]/15 text-[#C0392B] border-[#C0392B]/40",
];

export default function FunctionalForm({ data, onChange }) {
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  const setTask = (i, patch) => {
    const tasks = data.tasks.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    onChange({ ...data, tasks });
  };

  return (
    <div className="space-y-6" data-testid="functional-form">
      <Section title="Assessment Details" desc="Assessor-administered. Ask one question at a time; document what happens without help.">
        <Field label="Assessor name" tid="functional-assessor" value={data.assessor} onChange={set("assessor")} />
        <Field label="Assessment date" tid="functional-date" type="date" value={data.assessment_date} onChange={set("assessment_date")} />
      </Section>

      <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="font-heading text-lg font-semibold text-stone-900">Level of Care Scale</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {LOC_LABELS.map((l, i) => (
            <span key={l} className={`rounded-full border px-3 py-1 text-xs font-medium ${LOC_COLORS[i]}`}>{i} = {l}</span>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {data.tasks.map((task, i) => (
          <div key={task.name} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`task-${task.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-heading text-sm font-semibold text-stone-900">{i + 1}. {task.name}</p>
              <div className="flex gap-1.5">
                {LOC_LABELS.map((l, lvl) => (
                  <button key={lvl} type="button" onClick={() => setTask(i, { level: lvl })}
                    data-testid={`task-${i}-level-${lvl}`}
                    className={`h-8 w-8 rounded-md border text-sm font-semibold transition-all ${
                      task.level === lvl ? LOC_COLORS[lvl] + " ring-1 ring-offset-1 ring-stone-400" : "border-stone-200 bg-white text-stone-400 hover:border-stone-400 hover:text-stone-700"
                    }`}>
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input placeholder="Days/times needed" value={task.days_times} data-testid={`task-${i}-times`}
                onChange={(e) => setTask(i, { days_times: e.target.value })} className="h-9 border-stone-300 bg-white text-sm" />
              <Input placeholder="Notes (what happens without help)" value={task.notes} data-testid={`task-${i}-notes`}
                onChange={(e) => setTask(i, { notes: e.target.value })} className="h-9 border-stone-300 bg-white text-sm" />
            </div>
          </div>
        ))}
      </div>

      <Section title="General Notes">
        <div className="md:col-span-2">
          <Area label="Overall observations, fall risks, equipment needs" tid="functional-general-notes" value={data.general_notes} onChange={set("general_notes")} />
        </div>
      </Section>
    </div>
  );
}
